import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function PayrollCompensation() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [activeTab, setActiveTab] = useState('periods');

  // Data
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodDetail, setPeriodDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Filters
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState('');

  // Modals
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showMissingHourModal, setShowMissingHourModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Generate form
  const [genMonth, setGenMonth] = useState(new Date().getMonth() + 1);
  const [genYear, setGenYear] = useState(new Date().getFullYear());

  // Item form
  const [itemType, setItemType] = useState('addition');
  const [itemCategory, setItemCategory] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemAmount, setItemAmount] = useState('');

  // Missing hour form
  const [mhDecision, setMhDecision] = useState('ignore');
  const [mhHours, setMhHours] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const authCheck = useCallback((res) => {
    if (res.status === 401 || res.status === 403) { navigate('/login'); throw new Error('Unauthorized'); }
    return res;
  }, [navigate]);

  // ─── FETCH FUNCTIONS ───
  const fetchPeriods = useCallback(async () => {
    try {
      let url = `${API}/companies/${companyId}/payroll?year=${filterYear}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setPeriods(data.data || []);
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to load payroll periods'); }
  }, [companyId, filterYear, filterStatus, authCheck]);

  const fetchPeriodDetail = useCallback(async (periodId) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/${periodId}`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setPeriodDetail(data);
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to load period details'); }
  }, [companyId, authCheck]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/summary?year=${filterYear}`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setSummary(data);
    } catch (e) { if (e.message !== 'Unauthorized') console.error(e); }
  }, [companyId, filterYear, authCheck]);

  const fetchPendingActions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/pending-actions`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setPendingActions(data);
    } catch (e) { if (e.message !== 'Unauthorized') console.error(e); }
  }, [companyId, authCheck]);

  const fetchAuditHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/audit-history`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setAuditLogs(data.data || []);
    } catch (e) { if (e.message !== 'Unauthorized') console.error(e); }
  }, [companyId, authCheck]);

  useEffect(() => {
    if (!companyId) return;
    fetchPeriods();
    if (activeTab === 'summary') fetchSummary();
    if (activeTab === 'pending') fetchPendingActions();
    if (activeTab === 'audit') fetchAuditHistory();
  }, [companyId, activeTab, fetchPeriods, fetchSummary, fetchPendingActions, fetchAuditHistory]);

  // ─── ACTIONS ───
  const generatePayroll = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/generate`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: genMonth, year: genYear }),
      });
      authCheck(res);
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setSuccess(data.message);
      setShowGenerateModal(false);
      fetchPeriods();
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to generate payroll'); }
    finally { setLoading(false); }
  };

  const changeStatus = async (periodId, action) => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/${periodId}/${action}`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      authCheck(res);
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setSuccess(data.message);
      fetchPeriods();
      if (selectedPeriod?.id === periodId) fetchPeriodDetail(periodId);
    } catch (e) { if (e.message !== 'Unauthorized') setError(`Failed to ${action} payroll`); }
    finally { setLoading(false); }
  };

  const deletePeriod = async (periodId) => {
    if (!window.confirm('Delete this payroll period? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/${periodId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      authCheck(res);
      if (res.ok) { setSuccess('Period deleted'); fetchPeriods(); setSelectedPeriod(null); setPeriodDetail(null); }
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to delete'); }
  };

  const addItem = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/${selectedPeriod.id}/records/${selectedRecord.id}/items`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: itemType, category: itemCategory, description: itemDescription, amount: parseFloat(itemAmount) }),
      });
      authCheck(res);
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setSelectedRecord(data);
      setShowItemModal(false);
      setItemCategory(''); setItemDescription(''); setItemAmount('');
      fetchPeriodDetail(selectedPeriod.id);
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to add item'); }
    finally { setLoading(false); }
  };

  const removeItem = async (itemId) => {
    if (!selectedRecord || !window.confirm('Remove this item?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/${selectedPeriod.id}/records/${selectedRecord.id}/items/${itemId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      authCheck(res);
      if (res.ok) {
        const data = await res.json();
        setSelectedRecord(data);
        fetchPeriodDetail(selectedPeriod.id);
      }
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to remove item'); }
  };

  const submitMissingHourDecision = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/${selectedPeriod.id}/records/${selectedRecord.id}/missing-hour-decision`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: mhDecision, hours: parseFloat(mhHours) || 0 }),
      });
      authCheck(res);
      if (res.ok) {
        const data = await res.json();
        setSelectedRecord(data);
        setShowMissingHourModal(false);
        fetchPeriodDetail(selectedPeriod.id);
      }
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed'); }
    finally { setLoading(false); }
  };

  const openPeriod = (period) => {
    setSelectedPeriod(period);
    fetchPeriodDetail(period.id);
    setActiveTab('detail');
  };

  const openRecord = async (record) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/${selectedPeriod.id}/records/${record.id}`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setSelectedRecord(data);
      setShowRecordModal(true);
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to load record'); }
  };

  const exportPayroll = async (periodId) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/payroll/${periodId}/export`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      // CSV download
      if (data.rows && data.rows.length > 0) {
        const headers = Object.keys(data.rows[0]);
        const csv = [headers.join(','), ...data.rows.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `payroll_${data.period}.csv`; a.click();
      }
    } catch (e) { if (e.message !== 'Unauthorized') setError('Export failed'); }
  };

  // ─── STYLES ───
  const containerStyle = { padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: '1400px', margin: '0 auto' };
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' };
  const titleStyle = { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 };
  const btnStyle = (color = '#3b82f6') => ({ padding: '8px 16px', backgroundColor: color, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' });
  const btnSmStyle = (color = '#3b82f6') => ({ padding: '4px 10px', backgroundColor: color, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
  const tabStyle = (active) => ({ padding: '10px 20px', border: 'none', borderBottom: active ? '3px solid #3b82f6' : '3px solid transparent', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: active ? '600' : '400', color: active ? '#3b82f6' : '#64748b' });
  const cardStyle = { background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
  const thStyle = { padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase' };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalContent = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto' };
  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const selectStyle = { ...inputStyle };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' };
  const statusBadge = (status) => {
    const colors = { draft: '#f59e0b', reviewed: '#8b5cf6', approved: '#3b82f6', paid: '#10b981', locked: '#6b7280' };
    return { display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: `${colors[status] || '#94a3b8'}20`, color: colors[status] || '#94a3b8' };
  };
  const metricCard = (label, value, color = '#3b82f6') => (
    <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '16px', flex: '1', minWidth: '150px' }}>
      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: '700', color }}>{value}</div>
    </div>
  );
  const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const alertStyle = (type) => ({ padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', backgroundColor: type === 'error' ? '#fef2f2' : '#f0fdf4', color: type === 'error' ? '#dc2626' : '#16a34a', border: `1px solid ${type === 'error' ? '#fecaca' : '#bbf7d0'}` });

  // ─── RENDER ───
  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>Payroll & Compensation</h1>
        <button style={btnStyle('#3b82f6')} onClick={() => setShowGenerateModal(true)}>+ Generate Payroll</button>
      </div>

      {error && <div style={alertStyle('error')}>{error} <span style={{ cursor: 'pointer', float: 'right' }} onClick={() => setError('')}>✕</span></div>}
      {success && <div style={alertStyle('success')}>{success} <span style={{ cursor: 'pointer', float: 'right' }} onClick={() => setSuccess('')}>✕</span></div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #e2e8f0', marginBottom: '24px', overflowX: 'auto' }}>
        {['periods', 'detail', 'summary', 'pending', 'audit'].map(t => (
          <button key={t} style={tabStyle(activeTab === t)} onClick={() => setActiveTab(t)}>
            {t === 'periods' ? 'Payroll Periods' : t === 'detail' ? 'Period Detail' : t === 'summary' ? 'Summary' : t === 'pending' ? 'Pending Actions' : 'Audit History'}
          </button>
        ))}
      </div>

      {/* ═══ PERIODS TAB ═══ */}
      {activeTab === 'periods' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <select style={{ ...selectStyle, width: '120px' }} value={filterYear} onChange={e => setFilterYear(e.target.value)}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select style={{ ...selectStyle, width: '140px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              {['draft', 'reviewed', 'approved', 'paid', 'locked'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>

          <div style={cardStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Period</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Employees</th>
                  <th style={thStyle}>Base Pay</th>
                  <th style={thStyle}>Overtime</th>
                  <th style={thStyle}>Net Pay</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {periods.length === 0 && (
                  <tr><td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No payroll periods found</td></tr>
                )}
                {periods.map(p => (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => openPeriod(p)}>
                    <td style={tdStyle}>{['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][p.period_month]} {p.period_year}</td>
                    <td style={tdStyle}><span style={statusBadge(p.status)}>{p.status}{p.reopened ? ' ⟳' : ''}</span></td>
                    <td style={tdStyle}>{p.records_count}</td>
                    <td style={tdStyle}>${fmt(p.total_base_pay)}</td>
                    <td style={tdStyle}>${fmt(p.total_overtime)}</td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>${fmt(p.total_net_pay)}</td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {p.status === 'draft' && <button style={btnSmStyle('#8b5cf6')} onClick={() => changeStatus(p.id, 'review')}>Review</button>}
                        {p.status === 'reviewed' && <button style={btnSmStyle('#3b82f6')} onClick={() => changeStatus(p.id, 'approve')}>Approve</button>}
                        {p.status === 'approved' && <button style={btnSmStyle('#10b981')} onClick={() => changeStatus(p.id, 'mark-paid')}>Mark Paid</button>}
                        {p.status === 'paid' && <button style={btnSmStyle('#6b7280')} onClick={() => changeStatus(p.id, 'lock')}>Lock</button>}
                        {(p.status === 'paid' || p.status === 'locked') && <button style={btnSmStyle('#f59e0b')} onClick={() => changeStatus(p.id, 'reopen')}>Reopen</button>}
                        <button style={btnSmStyle('#0ea5e9')} onClick={() => exportPayroll(p.id)}>Export</button>
                        {p.status === 'draft' && <button style={btnSmStyle('#ef4444')} onClick={() => deletePeriod(p.id)}>Delete</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ DETAIL TAB ═══ */}
      {activeTab === 'detail' && periodDetail && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>
              {periodDetail.period?.period_label || `${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][periodDetail.period?.period_month]} ${periodDetail.period?.period_year}`}
              <span style={{ ...statusBadge(periodDetail.period?.status), marginLeft: '12px' }}>{periodDetail.period?.status}</span>
            </h2>
          </div>

          {/* Summary cards */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {metricCard('Employees', periodDetail.summary?.employee_count, '#3b82f6')}
            {metricCard('Base Pay', `$${fmt(periodDetail.summary?.total_base_pay)}`, '#0ea5e9')}
            {metricCard('Overtime', `$${fmt(periodDetail.summary?.total_overtime)}`, '#8b5cf6')}
            {metricCard('Additions', `$${fmt(periodDetail.summary?.total_additions)}`, '#10b981')}
            {metricCard('Deductions', `$${fmt(periodDetail.summary?.total_deductions)}`, '#ef4444')}
            {metricCard('Net Pay', `$${fmt(periodDetail.summary?.total_net_pay)}`, '#059669')}
          </div>

          {/* Records table */}
          <div style={{ ...cardStyle, overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Employee</th>
                  <th style={thStyle}>Dept</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Base Pay</th>
                  <th style={thStyle}>OT</th>
                  <th style={thStyle}>Transport</th>
                  <th style={thStyle}>+Adds</th>
                  <th style={thStyle}>-Deds</th>
                  <th style={thStyle}>Net Pay</th>
                  <th style={thStyle}>⚠</th>
                </tr>
              </thead>
              <tbody>
                {(periodDetail.period?.records || []).map(r => (
                  <tr key={r.id} style={{ cursor: 'pointer', backgroundColor: r.has_warnings ? '#fffbeb' : 'transparent' }} onClick={() => openRecord(r)}>
                    <td style={tdStyle}><strong>{r.employee_name}</strong><br /><span style={{ fontSize: '11px', color: '#94a3b8' }}>{r.employee_code}</span></td>
                    <td style={tdStyle}>{r.department_name || '—'}</td>
                    <td style={tdStyle}><span style={{ fontSize: '11px', textTransform: 'capitalize' }}>{r.compensation_type}</span></td>
                    <td style={tdStyle}>${fmt(r.base_pay)}</td>
                    <td style={tdStyle}>${fmt(r.overtime_amount)}</td>
                    <td style={tdStyle}>${fmt(r.transportation_amount)}</td>
                    <td style={{ ...tdStyle, color: '#16a34a' }}>+${fmt(r.gross_additions)}</td>
                    <td style={{ ...tdStyle, color: '#dc2626' }}>-${fmt(r.gross_deductions)}</td>
                    <td style={{ ...tdStyle, fontWeight: '700' }}>${fmt(r.net_pay)}</td>
                    <td style={tdStyle}>{r.has_warnings ? '⚠️' : '✓'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {activeTab === 'detail' && !periodDetail && (
        <div style={{ ...cardStyle, textAlign: 'center', color: '#94a3b8' }}>Select a payroll period from the Periods tab</div>
      )}

      {/* ═══ SUMMARY TAB ═══ */}
      {activeTab === 'summary' && summary && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {metricCard('Total Base Pay', `$${fmt(summary.total_base_pay)}`, '#0ea5e9')}
            {metricCard('Total Overtime', `$${fmt(summary.total_overtime)}`, '#8b5cf6')}
            {metricCard('Total Transport', `$${fmt(summary.total_transportation)}`, '#f59e0b')}
            {metricCard('Total Additions', `$${fmt(summary.total_additions)}`, '#10b981')}
            {metricCard('Total Deductions', `$${fmt(summary.total_deductions)}`, '#ef4444')}
            {metricCard('Total Net Pay', `$${fmt(summary.total_net_pay)}`, '#059669')}
          </div>

          {/* Department breakdown */}
          {summary.department_totals && Object.keys(summary.department_totals).length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1e293b' }}>Department Breakdown</h3>
              <table style={tableStyle}>
                <thead><tr><th style={thStyle}>Department</th><th style={thStyle}>Employees</th><th style={thStyle}>Net Pay</th></tr></thead>
                <tbody>
                  {Object.entries(summary.department_totals).map(([dept, data]) => (
                    <tr key={dept}><td style={tdStyle}>{dept || 'Unassigned'}</td><td style={tdStyle}>{data.employees}</td><td style={{ ...tdStyle, fontWeight: '600' }}>${fmt(data.net_pay)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Period breakdown */}
          {summary.periods && summary.periods.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1e293b' }}>Period Breakdown</h3>
              <table style={tableStyle}>
                <thead><tr><th style={thStyle}>Period</th><th style={thStyle}>Status</th><th style={thStyle}>Employees</th><th style={thStyle}>Net Pay</th></tr></thead>
                <tbody>
                  {summary.periods.map((p, i) => (
                    <tr key={i}><td style={tdStyle}>{p.label}</td><td style={tdStyle}><span style={statusBadge(p.status)}>{p.status}</span></td><td style={tdStyle}>{p.employees}</td><td style={{ ...tdStyle, fontWeight: '600' }}>${fmt(p.net_pay)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ PENDING ACTIONS TAB ═══ */}
      {activeTab === 'pending' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1e293b' }}>Pending Actions ({pendingActions.length})</h3>
          {pendingActions.length === 0 && <p style={{ color: '#94a3b8' }}>No pending actions</p>}
          {pendingActions.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', backgroundColor: a.type === 'warning' ? '#fef3c7' : a.type === 'adjustment' ? '#ede9fe' : '#dbeafe', color: a.type === 'warning' ? '#d97706' : a.type === 'adjustment' ? '#7c3aed' : '#2563eb', marginRight: '8px' }}>{a.type.replace('_', ' ')}</span>
                {a.message}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ AUDIT TAB ═══ */}
      {activeTab === 'audit' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1e293b' }}>Audit History</h3>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Date</th><th style={thStyle}>User</th><th style={thStyle}>Action</th><th style={thStyle}>Description</th></tr></thead>
            <tbody>
              {auditLogs.length === 0 && <tr><td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No audit logs</td></tr>}
              {auditLogs.map(log => (
                <tr key={log.id}>
                  <td style={tdStyle}>{new Date(log.created_at).toLocaleString()}</td>
                  <td style={tdStyle}>{log.user?.name || '—'}</td>
                  <td style={tdStyle}><span style={{ ...statusBadge('draft'), backgroundColor: '#f0f9ff', color: '#0369a1' }}>{log.action}</span></td>
                  <td style={tdStyle}>{log.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ GENERATE MODAL ═══ */}
      {showGenerateModal && (
        <div style={modalOverlay} onClick={() => setShowGenerateModal(false)}>
          <div style={modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: '18px' }}>Generate Payroll</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={labelStyle}>Month</label>
                <select style={selectStyle} value={genMonth} onChange={e => setGenMonth(parseInt(e.target.value))}>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Year</label>
                <select style={selectStyle} value={genYear} onChange={e => setGenYear(parseInt(e.target.value))}>
                  {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={btnStyle('#6b7280')} onClick={() => setShowGenerateModal(false)}>Cancel</button>
              <button style={btnStyle('#3b82f6')} onClick={generatePayroll} disabled={loading}>{loading ? 'Generating...' : 'Generate'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ RECORD DETAIL MODAL ═══ */}
      {showRecordModal && selectedRecord && (
        <div style={modalOverlay} onClick={() => setShowRecordModal(false)}>
          <div style={{ ...modalContent, maxWidth: '800px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>{selectedRecord.employee_name} — Payroll Breakdown</h2>
              <button style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowRecordModal(false)}>✕</button>
            </div>

            {/* Employee info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div><span style={{ fontSize: '12px', color: '#94a3b8' }}>Code</span><div style={{ fontWeight: '600' }}>{selectedRecord.employee_code}</div></div>
              <div><span style={{ fontSize: '12px', color: '#94a3b8' }}>Department</span><div style={{ fontWeight: '600' }}>{selectedRecord.department_name || '—'}</div></div>
              <div><span style={{ fontSize: '12px', color: '#94a3b8' }}>Type</span><div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{selectedRecord.compensation_type}</div></div>
            </div>

            {/* Pay breakdown */}
            <div style={{ ...cardStyle, backgroundColor: '#f8fafc' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#475569' }}>Pay Calculation</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                <div>Base Salary: <strong>${fmt(selectedRecord.base_salary)}</strong></div>
                <div>Salary Factor: <strong>{selectedRecord.salary_factor ?? '—'}</strong></div>
                <div>Scheduled Days: <strong>{selectedRecord.scheduled_days}</strong></div>
                <div>Worked Days: <strong>{selectedRecord.worked_days}</strong></div>
                <div>Scheduled Hours: <strong>{selectedRecord.scheduled_hours}</strong></div>
                <div>Worked Hours: <strong>{selectedRecord.worked_hours}</strong></div>
                <div>Paid Leave: <strong>{selectedRecord.paid_leave_days} days</strong></div>
                <div>Unpaid Leave: <strong>{selectedRecord.unpaid_leave_days} days</strong></div>
                <div>Overtime: <strong>{selectedRecord.overtime_hours} hrs → ${fmt(selectedRecord.overtime_amount)}</strong></div>
                <div>Transportation: <strong>{selectedRecord.transportation_days} days → ${fmt(selectedRecord.transportation_amount)}</strong></div>
              </div>
              <hr style={{ margin: '12px 0', borderColor: '#e2e8f0' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px', fontWeight: '600' }}>
                <div>Base Pay: ${fmt(selectedRecord.base_pay)}</div>
                <div>Gross Pay: ${fmt(selectedRecord.gross_pay)}</div>
                <div style={{ color: '#16a34a' }}>+ Additions: ${fmt(selectedRecord.gross_additions)}</div>
                <div style={{ color: '#dc2626' }}>- Deductions: ${fmt(selectedRecord.gross_deductions)}</div>
              </div>
              <div style={{ marginTop: '12px', fontSize: '20px', fontWeight: '700', color: '#059669' }}>Net Pay: ${fmt(selectedRecord.net_pay)}</div>
            </div>

            {/* Warnings */}
            {selectedRecord.has_warnings && selectedRecord.warnings && (
              <div style={{ ...alertStyle('error'), marginTop: '12px' }}>
                <strong>⚠ Warnings:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                  {selectedRecord.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {/* Items */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', color: '#475569' }}>Additions & Deductions</h4>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button style={btnSmStyle('#10b981')} onClick={() => { setItemType('addition'); setShowItemModal(true); }}>+ Addition</button>
                  <button style={btnSmStyle('#ef4444')} onClick={() => { setItemType('deduction'); setShowItemModal(true); }}>+ Deduction</button>
                  <button style={btnSmStyle('#f59e0b')} onClick={() => setShowMissingHourModal(true)}>Missing Hours</button>
                </div>
              </div>
              <table style={tableStyle}>
                <thead><tr><th style={thStyle}>Type</th><th style={thStyle}>Category</th><th style={thStyle}>Description</th><th style={thStyle}>Amount</th><th style={thStyle}></th></tr></thead>
                <tbody>
                  {(!selectedRecord.items || selectedRecord.items.length === 0) && <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No items</td></tr>}
                  {(selectedRecord.items || []).map(item => (
                    <tr key={item.id}>
                      <td style={tdStyle}><span style={{ color: item.type === 'addition' ? '#16a34a' : '#dc2626', fontWeight: '600' }}>{item.type === 'addition' ? '+' : '−'}</span></td>
                      <td style={tdStyle}>{item.category}</td>
                      <td style={tdStyle}>{item.description}</td>
                      <td style={{ ...tdStyle, fontWeight: '600', color: item.type === 'addition' ? '#16a34a' : '#dc2626' }}>${fmt(item.amount)}</td>
                      <td style={tdStyle}><button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }} onClick={() => removeItem(item.id)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ADD ITEM MODAL ═══ */}
      {showItemModal && (
        <div style={modalOverlay} onClick={() => setShowItemModal(false)}>
          <div style={{ ...modalContent, maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: '18px' }}>Add {itemType === 'addition' ? 'Addition' : 'Deduction'}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Category</label>
                <select style={selectStyle} value={itemCategory} onChange={e => setItemCategory(e.target.value)}>
                  <option value="">Select...</option>
                  {itemType === 'addition' ? (
                    <>
                      <option value="allowance">Allowance</option>
                      <option value="bonus">Bonus</option>
                      <option value="commission">Commission</option>
                      <option value="other">Other Addition</option>
                    </>
                  ) : (
                    <>
                      <option value="salary_advance">Salary Advance</option>
                      <option value="loan">Loan</option>
                      <option value="penalty">Penalty</option>
                      <option value="insurance">Insurance Deduction</option>
                      <option value="other">Other Deduction</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <input style={inputStyle} value={itemDescription} onChange={e => setItemDescription(e.target.value)} placeholder="Description" />
              </div>
              <div>
                <label style={labelStyle}>Amount</label>
                <input style={inputStyle} type="number" step="0.01" value={itemAmount} onChange={e => setItemAmount(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button style={btnStyle('#6b7280')} onClick={() => setShowItemModal(false)}>Cancel</button>
              <button style={btnStyle(itemType === 'addition' ? '#10b981' : '#ef4444')} onClick={addItem} disabled={loading || !itemCategory || !itemDescription || !itemAmount}>
                {loading ? 'Adding...' : `Add ${itemType === 'addition' ? 'Addition' : 'Deduction'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MISSING HOUR DECISION MODAL ═══ */}
      {showMissingHourModal && (
        <div style={modalOverlay} onClick={() => setShowMissingHourModal(false)}>
          <div style={{ ...modalContent, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: '18px' }}>Missing Hour Decision</h2>
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Decision</label>
              <select style={selectStyle} value={mhDecision} onChange={e => setMhDecision(e.target.value)}>
                <option value="ignore">Ignore (no deduction)</option>
                <option value="deduct">Deduct</option>
              </select>
            </div>
            {mhDecision === 'deduct' && (
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Hours to Deduct</label>
                <input style={inputStyle} type="number" step="0.5" value={mhHours} onChange={e => setMhHours(e.target.value)} placeholder="0" />
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={btnStyle('#6b7280')} onClick={() => setShowMissingHourModal(false)}>Cancel</button>
              <button style={btnStyle('#3b82f6')} onClick={submitMissingHourDecision} disabled={loading}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}